import React, { useState } from 'react';
import SearchableSelect from './SearchableSelect';
import { ProductForm } from './ProductManagement';
import WasteGradeModal from './WasteGradeModal';

export const GATE_PASS_TYPES = ['ENTRY', 'EXIT'];
export const MATERIAL_TYPES = ['RAW_MATERIAL', 'PACKAGING', 'READY_CONTAINER', 'WASTE', 'OTHER'];
// Who a gate pass can be with depends on the direction: goods coming in are from a supplier (or
// brought/handled by a transporter / contractor); goods going out are to a customer - never a supplier.
export const PARTY_TYPES_BY_DIRECTION = {
  ENTRY: ['SUPPLIER', 'TRANSPORTER', 'CONTRACTOR'],
  EXIT:  ['CUSTOMER', 'TRANSPORTER', 'CONTRACTOR'],
};
// The enum value stays WASTE in the database; this is only what people see.
export const MATERIAL_LABELS = { WASTE: 'Sellable Waste' };
export const materialLabel = (t) => MATERIAL_LABELS[t] || (t ? t.replace(/_/g, ' ') : '-');
export const UNITS = ['KG', 'TON', 'BAG', 'CONTAINER', 'PIECE', 'LITER', 'METER', 'BOX'];
export const CURRENCIES = ['PKR', 'USD', 'EUR', 'AED', 'GBP'];

const fmt = (str) => str ? str.replace(/_/g, ' ') : '—';

export const emptyItem = () => ({ itemName: '', itemCode: '', productId: null, quantity: '', unit: 'KG', weight: '', packSizeKg: null, purchasePrice: '', priceUnit: 'KG', priceRaw: '', purchaseCurrency: 'PKR', vendorPo: '', notes: '', bagTypeProductId: '' });

export const parsePackSizeKg = (packSize) => {
  if (!packSize) return null;
  const num = parseFloat(String(packSize).replace(/[^0-9.]/g, ''));
  if (isNaN(num) || num <= 0) return null;
  const lower = String(packSize).toLowerCase();
  if (lower.includes('ton')) return num * 1000;
  if (lower.includes('g') && !lower.includes('kg')) return num / 1000;
  return num; // KG or unitless — treat as KG
};

export const emptyForm = () => ({
  type: 'ENTRY',
  materialType: 'RAW_MATERIAL',
  gatePassDate: new Date().toISOString().split('T')[0],
  warehouseName: '',
  batchId: '',
  _partyId: '',
  partyName: '',
  partyType: 'SUPPLIER',
  vehicleNumber: '',
  driverName: '',
  driverContact: '',
  containerNumber: '',
  notes: '',
  serviceCharges: '',
  laborCost: '',
  items: [emptyItem()],
});

/**
 * Turns the form state into the payload POST /gate-passes expects. Shared so the Gate Pass page
 * and the "New Batch" pop-up (which embeds this same form) can never build it differently.
 */
export const buildGatePassPayload = (form) => ({
  ...form,
  batchId: form.batchId && form.batchId !== 'NEW' ? Number(form.batchId) : undefined,
  serviceCharges: form.serviceCharges ? parseFloat(form.serviceCharges) : undefined,
  laborCost: form.laborCost ? parseFloat(form.laborCost) : undefined,
  items: form.items.map(i => ({
    itemName: i.itemName,
    itemCode: i.itemCode || undefined,
    productId: i.productId || undefined,
    quantity: parseFloat(i.quantity),
    unit: i.unit,
    weight: i.weight ? parseFloat(i.weight) : undefined,
    purchasePrice: i.purchasePrice ? parseFloat(i.purchasePrice) : undefined,
    purchaseCurrency: i.purchaseCurrency || undefined,
    vendorPo: i.vendorPo || undefined,
    notes: i.notes || undefined,
    bagTypeProductId: i.bagTypeProductId ? Number(i.bagTypeProductId) : undefined,
  })),
});

/** Returns an error string, or null when the form is fine. */
export const validateGatePassItems = (form) => {
  for (const item of form.items) {
    if (!item.itemName.trim())                 return 'Each item must have a name';
    if (!item.quantity || +item.quantity <= 0) return 'Each item needs a valid quantity';
    if (!item.productId)                       return 'Select a product from the catalog for each item — required for stock tracking';
  }
  return null;
};

/**
 * The gate pass form fields (everything inside the <form>, no buttons).
 * batchMode: used inside the New Batch pop-up. The gate pass is locked to ENTRY, the batch does
 * not exist yet so there is no batch picker (form.batchId is the sentinel 'NEW', which keeps the
 * existing "product locked to batch" behaviour), and the product comes from the batch fields.
 */
const GatePassFormFields = ({ form, setForm, productCatalog, setProductCatalog, warehouseList, partyList, batchList = [], batchMode = false }) => {
  const [quickAddFor, setQuickAddFor] = useState(null);
  const [addGradeFor, setAddGradeFor] = useState(null);

  // Sellable Waste: the lines are waste grades (the "sub-category"), not the batch's own product.
  const isWaste = form.materialType === 'WASTE';
  const gradeProducts = productCatalog.filter(p => p.category === 'SELLABLE_WASTE');

  // Labor/service placeholder products (category CONSUMABLE) are cost entries, not physical
  // stock — they must never be receivable/dispatchable via a Gate Pass. Waste grades have their
  // own picker and must not show up among ordinary goods.
  const purchasableProducts = productCatalog.filter(p => p.category !== 'CONSUMABLE' && p.category !== 'SELLABLE_WASTE');

  // The batch's own product, used to re-lock the line when leaving Sellable Waste.
  const batchItems = (batchId) => {
    const b = batchList.find(x => String(x.id) === String(batchId));
    if (!b?.productId) return [emptyItem()];
    const prod = productCatalog.find(p => String(p.id) === String(b.productId));
    return [{
      ...emptyItem(),
      productId: b.productId,
      itemName: b.productName || (prod?.name || ''),
      itemCode: prod?.productCode || '',
      unit: prod?.unit || 'KG',
      packSizeKg: prod?.packSize ? parsePackSizeKg(prod.packSize) : null,
    }];
  };

  const changeMaterial = (next) => {
    setForm(f => {
      if (next === f.materialType) return f;
      if (next === 'WASTE') return { ...f, materialType: next, items: [{ ...emptyItem(), unit: 'KG' }] };
      if (f.materialType === 'WASTE') return { ...f, materialType: next, items: f.batchId && f.batchId !== 'NEW' ? batchItems(f.batchId) : [emptyItem()] };
      return { ...f, materialType: next };
    });
  };
  // Empty sacks (Bori/Katta) — real sellable Products tagged BYPRODUCT.
  const byproductProducts = productCatalog.filter(p => p.category === 'BYPRODUCT');

  const addItem    = () => setForm(f => ({ ...f, items: [...f.items, emptyItem()] }));
  const removeItem = (i) => setForm(f => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));
  const setItem = (i, k, v) => setForm(f => ({
    ...f,
    items: f.items.map((it, idx) => {
      if (idx !== i) return it;
      const updated = { ...it, [k]: v };
      const ps = updated.packSizeKg;
      // ONE direction only: bags typed → auto-fill weight (no reverse to prevent float drift)
      if (ps && ps > 0 && k === 'quantity' && v !== '') {
        const bags = parseFloat(v);
        if (!isNaN(bags) && bags > 0) {
          updated.weight = (Math.round(bags * ps * 1000) / 1000).toFixed(3);
        }
      }
      return updated;
    })
  }));

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // justCreated: a product added a moment ago is not in productCatalog yet (state not flushed)
  const selectProduct = (i, prodId, justCreated) => {
    if (!prodId) return;
    const product = justCreated || productCatalog.find(p => String(p.id) === String(prodId));
    if (!product) return;
    const psKg = parsePackSizeKg(product.packSize);
    setForm(f => ({
      ...f,
      items: f.items.map((it, idx) => idx === i ? {
        ...it,
        productId: Number(product.id),
        itemName: product.name,
        itemCode: product.productCode || it.itemCode,
        unit: product.unit || it.unit,
        packSizeKg: psKg,
      } : it),
    }));
  };

  const handleProductCreated = (itemIndex) => (product) => {
    setProductCatalog(prev => [...prev, product]);
    selectProduct(itemIndex, product.id);
    setQuickAddFor(null);
  };

  return (
    <>              <div className="form-grid-2">
                <div className="form-group">
                  <label className="form-label">Type *</label>
                  <select className="form-control" value={form.type} disabled={batchMode} onChange={e => {
                    const nextType = e.target.value;
                    setForm(f => ({
                      ...f,
                      type: nextType,
                      _partyId: '',
                      partyName: '',
                      partyType: nextType === 'ENTRY' ? 'SUPPLIER' : 'CUSTOMER',
                      // Waste is only ever sold, so it cannot stay selected on an ENTRY pass
                      ...(nextType === 'ENTRY' && f.materialType === 'WASTE'
                        ? { materialType: 'RAW_MATERIAL', items: f.batchId && f.batchId !== 'NEW' ? batchItems(f.batchId) : [emptyItem()] }
                        : {}),
                    }));
                  }}>
                    {GATE_PASS_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Material Type *</label>
                  <select className="form-control" value={form.materialType} onChange={e => changeMaterial(e.target.value)}>
                    {MATERIAL_TYPES.filter(t => !(form.type === 'ENTRY' && t === 'WASTE')).map(t => <option key={t} value={t}>{materialLabel(t)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date *</label>
                  <input type="date" className="form-control" required value={form.gatePassDate} onChange={e => setField('gatePassDate', e.target.value)} />
                </div>
                {batchMode ? (
                <div className="form-group">
                  <label className="form-label" style={{ color: 'var(--color-success)' }}>Batch Reference</label>
                  <div style={{ padding: '8px 12px', border: '1px solid var(--color-accent-hover)', borderRadius: 6, fontSize: 13, background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)', fontWeight: 500 }}>
                    The batch being created above — linked automatically
                  </div>
                </div>
                ) : (
                <div className="form-group">
                  <label className="form-label" style={{ color: !form.batchId ? 'var(--color-error)' : 'var(--color-success)' }}>
                    Batch Reference *
                    <span style={{ fontSize: 11, fontWeight: 400, marginLeft: 8, color: 'var(--color-text-secondary)' }}>
                      {form.type === 'ENTRY'
                        ? 'Create batch first in Batch Management, then come back here'
                        : 'Select the batch being dispatched'}
                    </span>
                  </label>
                  {batchList.length === 0 ? (
                    <div style={{ padding: '7px 10px', border: '1px solid var(--color-error)', borderRadius: 6, fontSize: 13, background: 'var(--color-error-light)', color: 'var(--color-error)' }}>
                      No batches found. Go to <strong>Batch Management → + New Batch</strong> first, then return here.
                    </div>
                  ) : (
                    <SearchableSelect
                      required
                      placeholder="— Select Batch (required) —"
                      value={form.batchId}
                      options={batchList.map(b => ({
                        value: b.id,
                        label: `${b.batchCode} · ${b.productName}${b.godownName ? ` · ${b.godownName}` : ''}`,
                      }))}
                      onChange={(value) => {
                        const id = String(value);
                        setField('batchId', id);
                        if (id) {
                          const b = batchList.find(x => String(x.id) === id);
                          // Auto-fill warehouse from batch
                          if (b?.godownName && !form.warehouseName) setField('warehouseName', b.godownName);
                          // Auto-fill items from batch product — lock them so user can't change product
                          if (b?.productId && !isWaste) {
                            const prod = productCatalog.find(p => String(p.id) === String(b.productId));
                            setForm(f => ({
                              ...f,
                              batchId: id,
                              items: [{
                                ...emptyItem(),
                                productId: b.productId,
                                itemName: b.productName || (prod?.name || ''),
                                itemCode: prod?.productCode || '',
                                unit: prod?.unit || 'KG',
                                packSizeKg: prod?.packSize ? parseFloat(prod.packSize) : null,
                              }]
                            }));
                          }
                        } else {
                          // Batch cleared — reset items
                          setForm(f => ({ ...f, batchId: '', items: [emptyItem()] }));
                        }
                      }}
                    />
                  )}
                </div>
                )}
                <div className="form-group">
                  <label className="form-label">Warehouse</label>
                  {warehouseList.length > 0 ? (
                    <select className="form-control" value={form.warehouseName} onChange={e => setField('warehouseName', e.target.value)}>
                      <option value="">— Select Warehouse —</option>
                      {warehouseList.map(w => <option key={w.id} value={w.name}>{w.name}{w.location ? ` (${w.location})` : ''}</option>)}
                      <option value="__custom__">Other (type below)</option>
                    </select>
                  ) : (
                    <input className="form-control" placeholder="e.g. Main Warehouse, Karachi Store" value={form.warehouseName} onChange={e => setField('warehouseName', e.target.value)} />
                  )}
                  {form.warehouseName === '__custom__' && (
                    <input className="form-control" style={{ marginTop: '6px' }} placeholder="Enter warehouse name..." onChange={e => setField('warehouseName', e.target.value)} autoFocus />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">
                    {form.type === 'ENTRY' ? 'Supplier / Transporter / Contractor *' : 'Customer *'}
                  </label>
                  {partyList.length > 0 ? (
                    <>
                      {(() => {
                        // One flat, searchable list beats native <select>'s three overlapping
                        // optgroups (Vendors/Customers/"All Parties" — the last one just
                        // re-listed everyone again as a safety net for mismatched customerType,
                        // so the same party could appear twice). Search makes that duplication
                        // unnecessary: sort the type that matches this gate pass direction to
                        // the top, label the rest with their type so they're still findable.
                        const primaryType = form.type === 'ENTRY' ? 'VENDOR' : 'CUSTOMER';
                        const isPrimary = (p) => p.customerType === primaryType || p.customerType === 'BOTH';
                        const sorted = [...partyList].sort((a, b) => {
                          const am = isPrimary(a), bm = isPrimary(b);
                          if (am !== bm) return am ? -1 : 1;
                          return (a.name || '').localeCompare(b.name || '');
                        });
                        const partyOptions = [
                          ...sorted.map(p => ({
                            value: p.id,
                            label: isPrimary(p) ? p.name : `${p.name} (${p.customerType})`,
                          })),
                          { value: '__other__', label: 'Type manually…' },
                        ];
                        return (
                          <SearchableSelect
                            placeholder="— Select —"
                            value={form._partyId || ''}
                            options={partyOptions}
                            onChange={(id) => {
                              if (id === '__other__') {
                                setField('_partyId', '__other__');
                                setField('partyName', '');
                              } else {
                                const p = partyList.find(x => String(x.id) === String(id));
                                if (p) {
                                  setField('_partyId', id);
                                  setField('partyName', p.name);
                                  // Suggest the party type from the record, but only one this direction allows
                                  const suggested = p.customerType === 'VENDOR' ? 'SUPPLIER' :
                                                    p.customerType === 'CUSTOMER' ? 'CUSTOMER' : null;
                                  const allowed = PARTY_TYPES_BY_DIRECTION[form.type] || [];
                                  setField('partyType', allowed.includes(suggested) ? suggested : allowed[0]);
                                }
                              }
                            }}
                          />
                        );
                      })()}
                      {form._partyId === '__other__' && (
                        <input
                          className="form-control"
                          style={{ marginTop: '6px' }}
                          placeholder="Enter party name..."
                          value={form.partyName}
                          onChange={e => setField('partyName', e.target.value)}
                          autoFocus
                        />
                      )}
                    </>
                  ) : (
                    <input
                      className="form-control"
                      placeholder="Enter party name (no parties in system yet)"
                      value={form.partyName}
                      onChange={e => setField('partyName', e.target.value)}
                    />
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Party Type *</label>
                  <select className="form-control" value={form.partyType} onChange={e => setField('partyType', e.target.value)}>
                    {(PARTY_TYPES_BY_DIRECTION[form.type] || []).map(t => <option key={t} value={t}>{fmt(t)}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Vehicle Number</label>
                  <input className="form-control" placeholder="e.g. LHR-1234" value={form.vehicleNumber} onChange={e => setField('vehicleNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Container Number</label>
                  <input className="form-control" placeholder="e.g. MSKU1234567" value={form.containerNumber} onChange={e => setField('containerNumber', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Driver Name</label>
                  <input className="form-control" placeholder="Driver full name" value={form.driverName} onChange={e => setField('driverName', e.target.value)} />
                </div>
                <div className="form-group">
                  <label className="form-label">Driver Contact</label>
                  <input className="form-control" placeholder="e.g. 0300-1234567" value={form.driverContact} onChange={e => setField('driverContact', e.target.value)} />
                </div>
              </div>

              {form.type === 'ENTRY' && (
                <div className="form-grid-2">
                  <div className="form-group">
                    <label className="form-label">Service Charges <span style={{fontWeight:400,color:'var(--color-text-secondary)'}}>(mandi commission, agent fee)</span></label>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={form.serviceCharges} onChange={e => setField('serviceCharges', e.target.value)} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Receiving Labor <span style={{fontWeight:400,color:'var(--color-text-secondary)'}}>(loading, mandi labor — same field as the batch's Sourcing tab)</span></label>
                    <input type="number" className="form-control" min="0" step="0.01" placeholder="0.00" value={form.laborCost} onChange={e => setField('laborCost', e.target.value)} />
                  </div>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} placeholder="Any additional notes" value={form.notes} onChange={e => setField('notes', e.target.value)} />
              </div>

              {/* Sellable Waste explainer */}
              {isWaste && (
                <div style={{ background: 'var(--color-info-light)', border: '1px solid var(--color-info)', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '13px', color: 'var(--color-info)' }}>
                  <strong>Sellable Waste:</strong> this is for selling material that was sorted out of the batch. Pick the waste grade (Chaant, Goli…) on each line — only grades already sorted out of a batch have stock to sell. Create the invoice from this gate pass to complete the sale.
                </div>
              )}

              {/* Items */}
              <div style={{ marginTop: '4px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <strong style={{ fontSize: '14px' }}>Items <span style={{ color: 'var(--color-error)' }}>*</span></strong>
                  {(!form.batchId || isWaste) && (
                    <button type="button" className="btn-secondary" style={{ fontSize: '12px', padding: '6px 14px' }} onClick={addItem}>
                      {isWaste ? '+ Add Grade' : '+ Add Item'}
                    </button>
                  )}
                  {form.batchId && !isWaste && (
                    <span style={{ fontSize: 12, color: 'var(--color-accent-hover)', fontStyle: 'italic' }}>
                      One product per batch — to add another product, use a separate gate pass with a different batch
                    </span>
                  )}
                </div>

                {form.items.map((item, i) => (
                  <div key={i} style={{ background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: '8px', padding: '14px', marginBottom: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, fontSize: '13px', color: 'var(--color-text-secondary)' }}>Item {i + 1}</span>
                      {form.items.length > 1 && (
                        <button type="button" onClick={() => removeItem(i)} style={{ background: 'none', border: 'none', color: 'var(--color-error)', cursor: 'pointer', fontSize: '12px', fontWeight: 500 }}>
                          ✕ Remove
                        </button>
                      )}
                    </div>
                    <div className="form-grid-2">
                      <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                        <label className="form-label" style={{ color: !item.productId ? 'var(--color-error)' : undefined }}>
                          {isWaste ? 'Waste grade (sub-category) *' : 'Product *'}
                          {isWaste
                            ? (item.productId
                                ? <span style={{ marginLeft: 8, color: 'var(--color-success)', fontSize: 11, fontWeight: 400 }}>✓ stock will be taken from this grade</span>
                                : <span style={{ marginLeft: 8, color: 'var(--color-error)', fontSize: 11, fontWeight: 400 }}>required</span>)
                          : item.productId && form.batchId
                            ? <span style={{ marginLeft: 8, color: 'var(--color-accent-hover)', fontSize: 11, fontWeight: 400 }}>Locked to batch — change batch to change product</span>
                            : item.productId
                              ? <span style={{ marginLeft: 8, color: 'var(--color-success)', fontSize: 11, fontWeight: 400 }}>✓ linked to catalog · stock will be tracked</span>
                              : <span style={{ marginLeft: 8, color: 'var(--color-error)', fontSize: 11, fontWeight: 400 }}>required — select batch above to auto-fill</span>}
                        </label>
                        {/* When batch is selected, product is locked — show as read-only badge */}
                        {isWaste ? (
                          <div style={{ display: 'flex', gap: 8 }}>
                            {gradeProducts.length === 0 ? (
                              <div style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--color-warning-border)', borderRadius: 6, fontSize: 13, background: 'var(--color-warning-light)', color: 'var(--color-warning-strong)' }}>
                                No waste grades yet — add one (e.g. Goli), or sort material out on a batch's Grading tab first.
                              </div>
                            ) : (
                              <SearchableSelect
                                required
                                placeholder="— Select waste grade —"
                                style={{ flex: 1 }}
                                value={item.productId || ''}
                                options={gradeProducts.map(p => ({
                                  value: p.id,
                                  label: `${p.name} · In stock: ${Number(p.currentStock || 0).toLocaleString()} kg`,
                                }))}
                                onChange={(value) => selectProduct(i, value)}
                              />
                            )}
                            <button type="button" className="btn-secondary" style={{ whiteSpace: 'nowrap', padding: '4px 12px' }} onClick={() => setAddGradeFor(i)}>
                              + New Grade
                            </button>
                          </div>
                        ) : form.batchId && item.productId ? (
                          <div style={{ padding: '8px 12px', border: '1px solid var(--color-accent-hover)', borderRadius: 6, fontSize: 13, background: 'var(--color-accent-light)', color: 'var(--color-accent-hover)', fontWeight: 500 }}>
                            {item.itemName}{item.itemCode ? ` [${item.itemCode}]` : ''} — product locked from batch
                          </div>
                        ) : (
                          <div style={{ display: 'flex', gap: 8 }}>
                            {purchasableProducts.length === 0 ? (
                              <div style={{ flex: 1, padding: '7px 10px', border: '1px solid var(--color-error)', borderRadius: 6, fontSize: 13, background: 'var(--color-error-light)', color: 'var(--color-error)' }}>
                                No products in catalog yet.
                              </div>
                            ) : (
                              <SearchableSelect
                                required
                                placeholder={form.batchId ? '— Select Batch above to auto-fill product —' : '— Select Product —'}
                                style={{ flex: 1 }}
                                value={item.productId || ''}
                                options={purchasableProducts.map(p => ({
                                  value: p.id,
                                  label: `${p.name}${p.productCode ? ` [${p.productCode}]` : ''}${p.packSize ? ` · ${p.packSize}` : ''}${p.currentStock != null ? ` · Stock: ${p.currentStock} ${p.unit || ''}` : ''}`,
                                }))}
                                onChange={(value) => selectProduct(i, value)}
                              />
                            )}
                            <button type="button" className="btn-secondary" style={{ whiteSpace: 'nowrap', padding: '4px 12px' }} onClick={() => setQuickAddFor(i)}>
                              + New Product
                            </button>
                          </div>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Item Name</label>
                        <input className="form-control" readOnly style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-neutral-700)' }} placeholder="Auto-filled from product" value={item.itemName} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Item Code</label>
                        <input className="form-control" readOnly style={{ background: 'var(--color-bg-tertiary)' }} placeholder="Auto-filled" value={item.itemCode} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Quantity (bags / units) *
                          {item.packSizeKg && <span style={{fontSize:'10px',color:'var(--color-success)',marginLeft:6}}>→ fills weight auto</span>}
                        </label>
                        <input type="number" className="form-control" required min="1" step="1" placeholder="e.g. 750" value={item.quantity} onChange={e => setItem(i, 'quantity', e.target.value)} />
                      </div>
                      <div className="form-group">
                        <label className="form-label">Unit *</label>
                        <select className="form-control" value={item.unit} onChange={e => setItem(i, 'unit', e.target.value)}>
                          {UNITS.map(u => <option key={u}>{u}</option>)}
                        </select>
                      </div>
                      <div className="form-group">
                        <label className="form-label">
                          Total Weight (KG)
                          {!item.packSizeKg && <span style={{fontSize:'10px',color:'var(--color-text-secondary)',marginLeft:6}}>— used for KG product stock</span>}
                        </label>
                        <input type="number" className="form-control" min="0" step="any" placeholder="e.g. 30000" value={item.weight} onChange={e => setItem(i, 'weight', e.target.value)} />
                        {item.packSizeKg && item.weight && parseFloat(item.weight) > 0 && (
                          <span style={{fontSize:'11px',color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                            ≈ {Math.round(parseFloat(item.weight) / item.packSizeKg).toLocaleString()} bags @ {item.packSizeKg} kg each
                          </span>
                        )}
                        {!item.packSizeKg && item.weight && item.quantity && parseFloat(item.weight) > 0 && parseFloat(item.quantity) > 0 && (
                          <span style={{fontSize:'11px',color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                            ≈ {(parseFloat(item.weight) / parseFloat(item.quantity)).toFixed(1)} kg/bag
                          </span>
                        )}
                      </div>
                      <div className="form-group">
                        <label className="form-label">Notes</label>
                        <input className="form-control" placeholder="Optional" value={item.notes} onChange={e => setItem(i, 'notes', e.target.value)} />
                      </div>
                      {form.type === 'ENTRY' && (
                        <>
                          <div className="form-group">
                            <label className="form-label">
                              Bag Type <span style={{fontSize:'10px',color:'var(--color-text-secondary)'}}>— optional, e.g. Katta/Bori this arrived in</span>
                            </label>
                            <select className="form-control" value={item.bagTypeProductId}
                              onChange={e => setItem(i, 'bagTypeProductId', e.target.value)}>
                              <option value="">— None —</option>
                              {byproductProducts.map(p => <option key={p.id} value={p.id}>{p.name}{p.packSize ? ` (${p.packSize})` : ''}</option>)}
                            </select>
                            {byproductProducts.length === 0 && (
                              <span style={{fontSize:11,color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                                No bag-type products yet — create one (e.g. "Katta", 10kg Bag) in Product Catalog, category Byproduct.
                              </span>
                            )}
                            {/* Credits Quantity directly, not weight ÷ pack size — Quantity IS the bag
                                count you counted for this shipment; deriving it from weight ÷ the bag
                                product's own nominal pack size instead used to silently disagree with
                                it (500 real bags could total 40,000kg — 80kg/bag average — even if the
                                selected "Katta" product is configured as 25kg, crediting 1,600 instead
                                of the 500 actually received). Caught live 2026-09-08. */}
                            {item.bagTypeProductId && item.quantity && parseFloat(item.quantity) > 0 && (() => {
                              const bagProduct = byproductProducts.find(p => String(p.id) === String(item.bagTypeProductId));
                              const bagCount = Math.floor(parseFloat(item.quantity));
                              // Quantity and Bag Type describe the SAME physical bags — Quantity is how
                              // many, Bag Type's pack size is how much each one holds — so their product
                              // should reasonably match Total Weight. A mismatch (e.g. 750 bags of a
                              // "10kg" bag type but 30,000kg total — implies 40kg/bag, not 10kg) usually
                              // means Quantity, Weight, or the Bag Type picked is wrong. Warn, don't
                              // block — real bags vary a bit, and this data doesn't need to be perfect
                              // to be useful, per the user's own call on this (2026-09-08).
                              const bagPackSizeKg = bagProduct ? parsePackSizeKg(bagProduct.packSize) : null;
                              const weightNum = parseFloat(item.weight);
                              let mismatchWarning = null;
                              if (bagPackSizeKg && weightNum > 0) {
                                const expectedWeight = bagCount * bagPackSizeKg;
                                const diffRatio = Math.abs(expectedWeight - weightNum) / weightNum;
                                if (diffRatio > 0.1) { // >10% off — flag it, don't block
                                  mismatchWarning = `${bagCount.toLocaleString()} bags × ${bagPackSizeKg}kg ≈ ${expectedWeight.toLocaleString()}kg, but Total Weight is ${weightNum.toLocaleString()}kg — double check Quantity, Weight, or Bag Type.`;
                                }
                              }
                              return (
                                <>
                                  <span style={{fontSize:11,color:'var(--color-success)',marginTop:2,display:'block'}}>
                                    ≈ {bagCount.toLocaleString()} {bagProduct?.name || 'bag'} bag{bagCount === 1 ? '' : 's'} will be added to inventory (free)
                                  </span>
                                  {mismatchWarning && (
                                    <span style={{fontSize:11,color:'var(--color-warning)',marginTop:2,display:'block'}}>
                                      ⚠ {mismatchWarning}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          <div className="form-group" style={item.unit === 'KG' ? { gridColumn: 'span 2' } : undefined}>
                            <label className="form-label">
                              Price <span style={{fontSize:'10px',color:'var(--color-text-secondary)'}}>— optional</span>
                              {!item.purchasePrice && <span style={{fontSize:11,color:'var(--color-text-secondary)',marginLeft:6,fontWeight:400}}>leave blank if not settled yet — add it later from the gate pass detail view</span>}
                            </label>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'stretch' }}>
                              {/* priceRaw holds exactly what's typed, in whatever unit is currently
                                  selected — kept separate from purchasePrice (always per-KG, the
                                  value actually submitted) so reformatting on toggle/blur never
                                  corrupts an in-progress keystroke (a real bug: re-deriving the
                                  displayed value from purchasePrice on every keystroke made typing
                                  "180" land as "1.0001" — the controlled value was overwriting
                                  itself mid-type). */}
                              <input type="number" className="form-control" min="0" step="any"
                                placeholder={item.priceUnit === 'MANN' ? 'e.g. 7200' : 'e.g. 180'}
                                style={{ flex: 1 }}
                                value={item.priceRaw}
                                onChange={e => {
                                  const typed = e.target.value;
                                  const perKg = typed === '' ? '' : (item.priceUnit === 'MANN' ? parseFloat(typed) / 40 : parseFloat(typed));
                                  setItem(i, 'priceRaw', typed);
                                  setItem(i, 'purchasePrice', typed === '' || isNaN(perKg) ? '' : perKg);
                                }} />
                              {item.unit === 'KG' && (
                                <div style={{ display: 'flex', border: '1px solid var(--color-border-strong)', borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                                  {[['KG', 'per KG'], ['MANN', 'per 40kg']].map(([val, label]) => (
                                    <button key={val} type="button"
                                      onClick={() => {
                                        // One-time conversion at the moment of switching — safe to
                                        // round here since it's not happening mid-keystroke.
                                        if (val !== item.priceUnit) {
                                          const price = parseFloat(item.purchasePrice);
                                          const nextRaw = isNaN(price) ? '' : (val === 'MANN'
                                            ? (price * 40).toFixed(2).replace(/\.?0+$/, '')
                                            : String(price));
                                          setItem(i, 'priceRaw', nextRaw);
                                        }
                                        setItem(i, 'priceUnit', val);
                                      }}
                                      style={{
                                        padding: '0 10px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                                        background: item.priceUnit === val ? 'var(--color-primary)' : 'var(--color-bg-tertiary)',
                                        color: item.priceUnit === val ? '#fff' : 'var(--color-neutral-700)',
                                      }}>
                                      {label}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                            {item.priceUnit === 'MANN' && item.purchasePrice && (
                              <span style={{fontSize:11,color:'var(--color-text-secondary)',marginTop:2,display:'block'}}>
                                = PKR {parseFloat(item.purchasePrice).toFixed(2)} per KG — same price, just entered per 40kg (mann)
                              </span>
                            )}
                          </div>
                          <div className="form-group">
                            <label className="form-label">Currency</label>
                            <select className="form-control" value={item.purchaseCurrency} onChange={e => setItem(i, 'purchaseCurrency', e.target.value)}>
                              {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                            </select>
                          </div>
                          {item.purchasePrice && (item.quantity || item.weight) && (() => {
                            const useWeight = item.weight && parseFloat(item.weight) > 0;
                            const base = useWeight ? parseFloat(item.weight) : parseFloat(item.quantity);
                            const total = isNaN(base) ? 0 : base * parseFloat(item.purchasePrice);
                            return (
                              <div className="form-group">
                                <label className="form-label">
                                  Line Total
                                  <span style={{fontSize:10,color:'var(--color-text-secondary)',marginLeft:6}}>
                                    {useWeight ? `${Number(item.weight).toLocaleString()} kg × PKR ${item.purchasePrice}/kg` : `${item.quantity} units × PKR ${item.purchasePrice}`}
                                  </span>
                                </label>
                                <input className="form-control" readOnly style={{ background: 'var(--color-bg-tertiary)', fontWeight: 600 }}
                                  value={`${item.purchaseCurrency || 'PKR'} ${total.toLocaleString('en-PK', { maximumFractionDigits: 0 })}`} />
                              </div>
                            );
                          })()}
                          <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                            <label className="form-label">Vendor PO #</label>
                            <input className="form-control" placeholder="Purchase order / reference number" value={item.vendorPo} onChange={e => setItem(i, 'vendorPo', e.target.value)} />
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
      {/* ── Quick Add Product Modal (from within an item row) ── */}
      {quickAddFor !== null && (
        <ProductForm
          vendors={partyList.filter(p => p.customerType === 'VENDOR' || p.customerType === 'BOTH')}
          excludeCategories={['CONSUMABLE', 'SELLABLE_WASTE']}
          onClose={() => setQuickAddFor(null)}
          onSave={handleProductCreated(quickAddFor)}
        />
      )}
      {addGradeFor !== null && (
        <WasteGradeModal
          existing={gradeProducts}
          onClose={() => setAddGradeFor(null)}
          onCreated={(product) => {
            setProductCatalog(prev => [...prev, product]);
            selectProduct(addGradeFor, product.id, product);
            setAddGradeFor(null);
          }}
        />
      )}
    </>
  );
};

export default GatePassFormFields;